"use client";

import { useRef, useState } from "react";
import { makeFunctionReference } from "convex/server";
import { useMutation } from "convex/react";
import { Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const createUploadRequest = makeFunctionReference<
  "mutation",
  { tenantId: string; loadId: string; fileName: string; actorId?: string },
  { documentId: string }
>("podUploads:createUploadRequest");

const createDriverUploadLink = makeFunctionReference<
  "mutation",
  { tenantId: string; loadId: string; driverPhone?: string; actorId?: string },
  { linkId: string; token: string; urlPath: string }
>("podUploads:createDriverUploadLink");

const generateUploadUrl = makeFunctionReference<
  "mutation",
  { tenantId: string; token?: string },
  { uploadUrl: string }
>("documentStorage:generateUploadUrl");

const recordUploadedFile = makeFunctionReference<
  "mutation",
  { tenantId: string; storageId: string; fileName: string; documentId?: string; token?: string; actorId?: string },
  { documentId: string; loadId: string; ocrScheduled: boolean }
>("documentStorage:recordUploadedFile");

export function PodUploadPanel({ tenantId, loadId }: { tenantId: string; loadId: string }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [status, setStatus] = useState("No POD uploaded");
  const [uploading, setUploading] = useState(false);
  const createUpload = useMutation(createUploadRequest);
  const createDriverLink = useMutation(createDriverUploadLink);
  const generateUrl = useMutation(generateUploadUrl);
  const recordUpload = useMutation(recordUploadedFile);

  async function handleFile(file?: File) {
    if (!file || uploading) return;
    if (!process.env.NEXT_PUBLIC_CONVEX_URL) {
      setStatus(`Demo POD staged: ${file.name}`);
      return;
    }
    setUploading(true);
    setStatus(`Uploading ${file.name}…`);
    try {
      const { documentId } = await createUpload({
        tenantId,
        loadId,
        fileName: file.name,
        actorId: "current-user",
      });
      const { uploadUrl } = await generateUrl({ tenantId });
      const post = await fetch(uploadUrl, {
        method: "POST",
        headers: { "Content-Type": file.type || "application/octet-stream" },
        body: file,
      });
      if (!post.ok) throw new Error(`storage upload failed (${post.status})`);
      const { storageId } = (await post.json()) as { storageId: string };
      await recordUpload({
        tenantId,
        documentId,
        storageId,
        fileName: file.name,
        actorId: "current-user",
      });
      setStatus(`POD stored — vision OCR queued (${documentId})`);
    } catch (error) {
      setStatus(`Upload failed: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setUploading(false);
    }
  }

  async function handleDriverLink() {
    if (!process.env.NEXT_PUBLIC_CONVEX_URL) {
      setStatus("Demo driver upload link: /upload/pod/demo");
      return;
    }
    const result = await createDriverLink({ tenantId, loadId, actorId: "current-user" });
    setStatus(`Driver upload link: ${result.urlPath}`);
  }

  return (
    <Card>
      <CardHeader className="p-3">
        <CardTitle className="text-sm">POD Upload</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 p-3 text-sm">
        <button
          className="flex min-h-24 w-full flex-col items-center justify-center gap-2 rounded-md border border-dashed border-border bg-card px-3 py-4 text-muted-foreground hover:bg-muted disabled:opacity-60"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
          onDrop={(event) => {
            event.preventDefault();
            void handleFile(event.dataTransfer.files[0]);
          }}
          onDragOver={(event) => event.preventDefault()}
        >
          <Upload size={18} />
          <span>{uploading ? "Uploading…" : "Drop POD PDF/image or browse"}</span>
        </button>
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          accept="application/pdf,image/*"
          onChange={(event) => void handleFile(event.target.files?.[0])}
        />
        <div className="text-xs text-muted-foreground">{status}</div>
        <Button className="h-8" variant="secondary" onClick={handleDriverLink}>
          Create driver upload link
        </Button>
      </CardContent>
    </Card>
  );
}
